import { useEffect, useState, useMemo, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from 'react-helmet';
import * as MUI from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { ruRU } from '@mui/x-data-grid';

import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import api from './api';
import Toolbar from './Toolbar';
import Sidebar from './Sidebar';
import LoginForm from './LoginForm';

import Dashboard from './pages/Dashboard';
import Services from './pages/Services';

const drawerWidth = 240;

export default function App() {
	const location = useLocation();
	const navigate = useNavigate();
	const [account, setAccount] = useState(false);
	const [error, setError] = useState(false);
	const [showLoginForm, setShowLoginForm] = useState(true);
	const [themeMode, setThemeMode] = useState('system');

	api.showError = (err) => {
		setError(err)
	}

	const start = async (startParams = {}) => {
		try {
			setShowLoginForm(false);
			setAccount(false);

			const response = await api.call('client/login', startParams);
			if (response.token) {
				// Successfully logged in
				api.setToken(response.token);
				setAccount(response.account);
			} else {
				setShowLoginForm(true);
			}
		} catch(e) {
			setShowLoginForm(true);
		}
	}

	useEffect(() => {
		if (!account) {
			const mode = localStorage.getItem('lk-theme') || 'system';
			setThemeMode(mode);
		}

		if (window.location.pathname === '/auth/token') {
			const token = window.location.search.substring(1);
			start({token: token});
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const theme = useMemo(() => {
		let mode = themeMode;
		if (mode === 'system') {
			var mql = window.matchMedia('(prefers-color-scheme: dark)');
			mode = mql.matches ? 'dark' : 'light';
		}
		return createTheme({
			palette: {
				mode: mode,
				primary: { main: '#fbc02d' },
				secondary: { main: '#424a5d' }
			},
		}, ruRU);
	}, [themeMode]);

	const setLightMode = (mode) => {
		setThemeMode(mode);
		localStorage.setItem('lk-theme', mode);
	}

	const atleastMedium = MUI.useMediaQuery(theme.breakpoints.up('md'));

	return (
		<ThemeProvider theme={theme}>

		<Helmet titleTemplate="%s — Личный кабинет">
			<title>?</title>
		</Helmet>

		<MUI.Box sx={{ display: 'flex' }}>
		<MUI.CssBaseline />
		{account && (<>
			<MUI.AppBar position="fixed" color="secondary">
				<Toolbar account={account} themeMode={themeMode} setThemeMode={setLightMode} />
			</MUI.AppBar>
			<MUI.Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
				<MUI.Drawer
					variant={atleastMedium ? "permanent" : "temporary"}
					sx={{ '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, zIndex: 1000 } }}
					open={location.search === '?leftMenu'}
					onClose={() => navigate(-1)}
					ModalProps={{ keepMounted: true }}
				>
					<Sidebar />
				</MUI.Drawer>
			</MUI.Box>
			<MUI.Box component="main"
				sx={{ flexGrow: 1, width: { md: `calc(100% - ${drawerWidth}px)` }, minHeight: '100vh', display: 'flex', flexDirection: 'column' }} >
				<MUI.Toolbar sx={{ mb: 0.5 }} />
				<Suspense fallback={
					<MUI.Box display="flex" alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
						<MUI.CircularProgress />
					</MUI.Box>
				}>
					<Routes>
						<Route exact path="/" element={<Dashboard />} />
						<Route path="/services/:id" element={<Services />} />
						<Route path="*" element={<Navigate to="/" replace />} />
					</Routes>
				</Suspense>
			</MUI.Box>
		</>)}
		{showLoginForm && (<LoginForm start={start} />)}
		<MUI.Snackbar open={error !== false} autoHideDuration={6000} onClose={() => setError(false)}>
			<MUI.Alert severity="error" variant="filled">{error}</MUI.Alert>
		</MUI.Snackbar>
	</MUI.Box>

	</ThemeProvider>
	);
}
